import { useState, useCallback } from "react";
import firebase from "../../firebase/compat";
import { client } from "../../hooks/Client";

/**
 * Drives the propose → review → save flow for mechanisms of change.
 *
 * - propose(): asks Decca to code the interview (read-only Cloud Function);
 *   returns proposals into state for the review screen.
 * - save(accepted): persists the user-approved proposals (dedup + write).
 *
 * Both call the europe-west1 v2 functions and pass `callerCompanyId` as the
 * tenant hint; the functions verify membership server-side.
 */
const useMechanismProposals = (interviewId) => {
    const [proposals, setProposals] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | proposing | ready | saving | saved | error
    const [error, setError] = useState("");

    const propose = useCallback(async () => {
        if (!interviewId) return;
        setStatus("proposing");
        setError("");
        try {
            const fn = firebase.app().functions("europe-west1").httpsCallable("proposeMechanisms");
            const res = await fn({ interviewId, callerCompanyId: client });
            setProposals((res.data && res.data.proposals) || []);
            setStatus("ready");
        } catch (err) {
            setError(err.message || "Onbekende fout");
            setStatus("error");
        }
    }, [interviewId]);

    const save = useCallback(
        async (accepted) => {
            setStatus("saving");
            setError("");
            try {
                const fn = firebase.app().functions("europe-west1").httpsCallable("saveMechanisms");
                const res = await fn({ interviewId, callerCompanyId: client, accepted });
                setStatus("saved");
                return res.data;
            } catch (err) {
                setError(err.message || "Onbekende fout");
                setStatus("error");
                throw err;
            }
        },
        [interviewId]
    );

    return { proposals, status, error, propose, save };
};

export default useMechanismProposals;
